import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, AlertCircle, Info, AlertTriangle, X, Check, BellRing, BellOff } from 'lucide-react';
import { useNotifications, type AppNotification } from '../../contexts/NotificationContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Button } from '../shared';

const ICONS: Record<AppNotification['type'], typeof Info> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

const ICON_COLORS: Record<AppNotification['type'], string> = {
  info: 'text-info-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  error: 'text-danger-500',
};

const BG_COLORS: Record<AppNotification['type'], string> = {
  info: 'bg-info-50',
  success: 'bg-success-50',
  warning: 'bg-warning-50',
  error: 'bg-danger-50',
};

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, dismissNotification, clearAll } =
    useNotifications();
  const push = usePushNotifications();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = (notif: AppNotification) => {
    markRead(notif.id);
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        type="button"
        variant="ghost"
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 text-surface-600 hover:text-surface-900 hover:bg-surface-100 rounded-lg"
      >
        <Bell className="w-5 h-5" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-danger-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-surface-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-100 bg-surface-50">
            <h3 className="text-sm font-semibold text-surface-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  onClick={markAllRead}
                  icon={<Check className="w-3 h-3" />}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  onClick={clearAll}
                  className="!text-surface-500 hover:!text-surface-700"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Push notification toggle */}
          {push.isSupported && (
            <div className="px-4 py-2.5 border-b border-surface-100 flex items-center justify-between">
              {push.permission === 'denied' ? (
                <p className="text-xs text-surface-500">
                  Push notifications are blocked. Enable them in your browser settings.
                </p>
              ) : push.isSubscribed ? (
                <>
                  <span className="text-xs text-success-600 flex items-center gap-1.5">
                    <BellRing className="w-3.5 h-3.5" />
                    Push notifications active
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    size="xs"
                    onClick={push.unsubscribe}
                    disabled={push.isLoading}
                    className="!text-surface-500 hover:!text-surface-700"
                  >
                    Disable
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-xs text-surface-500 flex items-center gap-1.5">
                    <BellOff className="w-3.5 h-3.5" />
                    Get push alerts for handovers
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    size="xs"
                    onClick={push.subscribe}
                    disabled={push.isLoading}
                  >
                    Enable
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-surface-300 mx-auto mb-2" />
                <p className="text-sm text-surface-500">No notifications</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = ICONS[notif.type];
                return (
                  <div
                    key={notif.id}
                    role="button"
                    tabIndex={0}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-surface-100 cursor-pointer hover:bg-surface-50 transition-colors ${
                      !notif.read ? BG_COLORS[notif.type] : ''
                    }`}
                    onClick={() => handleClick(notif)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(notif); } }}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ICON_COLORS[notif.type]}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.read ? 'font-medium text-surface-900' : 'text-surface-700'}`}>
                        {notif.title}
                      </p>
                      {notif.description && (
                        <p className="text-xs text-surface-500 mt-0.5 line-clamp-2">
                          {notif.description}
                        </p>
                      )}
                      <p className="text-xs text-surface-400 mt-1">{timeAgo(notif.createdAt)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      aria-label="Dismiss notification"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotification(notif.id);
                      }}
                      className="p-1 text-surface-400 hover:text-surface-600 flex-shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
